import { motion } from "framer-motion";
import { Droplets, Filter, Sun, Waves, FlaskConical, Mountain, Wrench, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { services, images } from "@/data/content";

const iconMap = { Droplets, Filter, Sun, Waves, FlaskConical, Mountain, Wrench };

const spanClass = {
  "tank-cleaning": "md:col-span-12 lg:col-span-7",
  filtration: "md:col-span-6 lg:col-span-5",
  uv: "md:col-span-6 lg:col-span-4",
  biocleanse: "md:col-span-6 lg:col-span-4",
  testing: "md:col-span-6 lg:col-span-4",
  bore: "md:col-span-6 lg:col-span-6",
  servicing: "md:col-span-6 lg:col-span-6",
};

function ServiceCard({ s, onQuote }) {
  const Icon = iconMap[s.icon];
  const featured = s.id === "tank-cleaning";
  const withImg = s.id === "filtration";
  const withLogo = s.id === "biocleanse";
  const withUv = s.id === "uv";
  const withAware = s.id === "testing";

  return (
    <motion.button
      onClick={onQuote}
      data-testid={`service-card-${s.id}`}
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className={`group text-left relative flex flex-col overflow-hidden rounded-[1.6rem] border border-pristine-aqua bg-white p-7 shadow-[0_8px_32px_rgba(15,58,112,0.05)] hover:shadow-[0_20px_50px_rgba(15,58,112,0.12)] transition-shadow ${spanClass[s.id]} ${featured ? "navy-gradient border-transparent" : ""}`}
    >
      {featured && (
        <img
          src={images.serviceTech}
          alt="Pristine Water Systems technician cleaning a residential rainwater tank"
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
      )}
      <div className="relative z-10 flex flex-col h-full">
        {withImg ? (
          <div className="-mx-7 -mt-7 mb-6 overflow-hidden aspect-[16/9]">
            <img src={images.filtrationTanks} alt="Whole-of-house water filtration system unit" className="w-full h-full object-cover" />
          </div>
        ) : withUv ? (
          <div className="-mx-7 -mt-7 mb-6 h-32 overflow-hidden">
            <img src={images.uvBanner} alt="Protect-UV whole of house filtration" className="w-full h-full object-cover" />
          </div>
        ) : withAware ? (
          <div className="-mx-7 -mt-7 mb-6 h-32 bg-pristine-aquamid px-6 flex items-center justify-center">
            <img src={images.awareLogo} alt="AWARE Water Testing" className="h-24 w-auto max-w-full mix-blend-multiply" />
          </div>
        ) : withLogo ? (
          <div className="-mx-7 -mt-7 mb-6 h-32 bg-pristine-aquamid px-6 flex items-center justify-center">
            <img src={images.biocleanseLogo} alt="AqueousPro BioCleanse" className="h-24 w-auto max-w-full mix-blend-multiply" />
          </div>
        ) : (
          <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl mb-6 ${featured ? "bg-white/15" : "bg-pristine-aqua"}`}>
            <Icon className={`h-6 w-6 ${featured ? "text-white" : "text-pristine-blue"}`} strokeWidth={1.7} />
          </div>
        )}

        <h3 className={`font-heading font-medium tracking-tight ${featured ? "text-white text-2xl sm:text-3xl" : "text-pristine-navy text-xl"}`}>
          {s.title}
        </h3>
        <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-white/80 max-w-md" : "text-pristine-navy/65"}`}>
          {s.body}
        </p>

        <span className={`mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${featured ? "text-pristine-aquamid" : "text-pristine-blue"}`}>
          {s.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </motion.button>
  );
}

export default function Services({ onQuote }) {
  return (
    <section id="services" className="py-14 lg:py-20 bg-pristine-surface" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-pristine-aquadeep mb-4">Our Services</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-pristine-navy tracking-tight text-balance">
            Complete Water Care Solutions
          </h2>
          <p className="mt-5 text-base sm:text-lg text-pristine-navy/70 leading-relaxed">
            From tank cleaning to filtration, UV protection, testing and ongoing servicing — we look
            after every part of your household water system.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {services.map((s) => (
            <ServiceCard key={s.id} s={s} onQuote={onQuote} />
          ))}
        </div>
      </div>
    </section>
  );
}
